import { getMediaType, isArchiveMemberSearchablePath } from './functions';
import { PROVENANCE_FILE_INFO_BATCH_LIMIT, type FileSummary } from './cors-provenance-api';
import type { MediaSource, Slide } from './types';

/** Resolves provenance ``file-info`` for a storage path. Content-moderation safe (no ``bypass_cm``). */
export type TimelineFileInfoFetcher = (url: string) => Promise<FileSummary | null>;

/**
 * Resolves provenance ``file-info`` for many storage paths in one request, keyed
 * by each path as passed in. Throws on a failed request; a rejection carrying a
 * ``status`` of 400 or 404 means one path in the batch was bad. ``null`` means
 * the batch route did not answer with a batch envelope, and the per-URL fetcher
 * should run instead.
 */
export type TimelineFileInfoBatchFetcher = (
  urls: string[],
) => Promise<Map<string, FileSummary | null> | null>;

export type ModerationLookupOptions = {
  concurrency?: number;
  /**
   * Resolve through the batch lookup instead of one request per path. A
   * timeline with 60 stills is then one request per load, not 60 (TR-3920).
   */
  fetchFileInfoBatch?: TimelineFileInfoBatchFetcher;
};

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * True for a reference provenance cannot resolve: already-fetchable URLs, inline
 * payloads, and virtual ``…/archive.zip/inner.jpg`` searchable paths, which are
 * not stored objects and answer 404.
 */
const isUnresolvable = (url: string): boolean =>
  /^(?:https?:|data:|blob:)/i.test(url) || !url.includes('/') || isArchiveMemberSearchablePath(url);

/**
 * The URL to ask provenance about for a saved media row, or ``''`` to leave the
 * row alone.
 *
 * Only stills are considered. Obfuscation is produced for images — there is no
 * obfuscated rendition of a video — so an image is the one row type whose
 * rendition depends on the review state. Restricting the lookup also keeps every
 * other type on exactly the request sequence it has today, which matters because
 * a provenance ``previewUrl`` is a *different kind of thing* per media type: a
 * markdown conversion for a document, a transcode for video. Neither is a
 * drop-in for what those rows already render.
 *
 * The stored display URL is the lookup key. It addresses a real processed object
 * — either the plain rendition or the obfuscated one — and provenance resolves
 * either back to the file's current finding, so no additional identity has to be
 * persisted alongside it and events saved before this change resolve too.
 *
 * Only ``mediaUrl`` / ``mediaSrc`` are consulted, because those are the two
 * fields {@link withRendition} rewrites and the same two ``getMediaSourceUrlKey``
 * derives a row's identity from. Looking a row up through some *other* field
 * would resolve a rendition this pass cannot then move the row onto.
 */
export function timelineModerationLookupUrl(media: MediaSource): string {
  if (getMediaType(media) !== 'image') return '';
  const stored = trimmed(media.mediaUrl) || trimmed(media.mediaSrc);
  return stored && !isUnresolvable(stored) ? stored : '';
}

/**
 * A file provenance has a content-moderation finding for, in any review state.
 *
 * ``REVIEWED`` counts: a cleared finding is exactly the case where the row was
 * saved blurred and must now render the real image, and the summary that says so
 * is the one carrying the finding.
 */
const hasModerationFinding = (info: FileSummary | null): boolean =>
  Boolean(info?.contentModeration?.type);

/**
 * Point a media row at ``rendition`` and attach the summary that resolved it.
 *
 * Every URL-bearing field moves together so the player, which streams
 * ``mediaUrl`` / ``mediaSrc`` (and ``mediaContext.previewUrl``), fetches the
 * rendition. Clip identity ignores a resolved ``srcToUse`` once those fields
 * are set (TR-3842), but ``previewFilePath`` still participates in
 * ``getMediaSourceUrlKey``. A row whose own URLs stay on the original object
 * while ``previewFilePath`` names a different rendition never becomes clip
 * aligned, and the cell sits on its loading spinner.
 *
 * ``previewFilePath`` is deliberately left alone: it feeds the *other* half of
 * that key and would reintroduce the mismatch it is meant to avoid.
 */
const withRendition = (
  media: MediaSource,
  rendition: string,
  info: FileSummary,
): MediaSource =>
  ({
    ...media,
    mediaUrl: rendition,
    mediaSrc: rendition,
    mediaContext: {
      ...((media.mediaContext ?? {}) as Record<string, unknown>),
      previewUrl: rendition,
    },
    // Carries ``contentModeration``, which is what mounts the Obfuscated /
    // Original switcher when the event is reopened in the viewer.
    fileInfo: info,
  }) as MediaSource;

const DEFAULT_CONCURRENCY = 6;

/**
 * Runs ``task`` over ``items`` at most ``limit`` at a time.
 *
 * Used only when the batch route is unavailable. A timeline can hold hundreds
 * of media rows; firing every lookup at once would spend the browser's
 * connection budget for the case view on this one pass.
 */
const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await task(items[index]);
    }
  });
  await Promise.all(workers);
  return results;
};

/** A batch the server failed because of one of its paths, not as a whole. */
const isRejectedPath = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 400 || status === 404;
};

/**
 * Summaries for ``urls`` through the batch lookup, ``null`` for any path that
 * did not resolve.
 *
 * The server fails a whole batch on one bad path, so such a batch is split in
 * half and each half retried, down to the single bad path. One deleted file
 * then costs a few extra requests rather than every still on the timeline
 * losing its summary. Any other failure (403, 429, 5xx, network) is not retried:
 * every path in the batch degrades to ``null``, the same as a failed GET. A
 * ``null`` result means the batch route is absent, so those paths fall back to
 * one lookup each. Do not delete the batch call and leave that per-URL GET as
 * the way a timeline or chat-media list is loaded.
 */
async function fetchBatchSummaries(
  urls: string[],
  fetchFileInfo: TimelineFileInfoFetcher,
  fetchFileInfoBatch: TimelineFileInfoBatchFetcher,
  concurrency: number,
): Promise<Map<string, FileSummary | null>> {
  const summaries = new Map<string, FileSummary | null>();
  const run = async (batch: string[]): Promise<void> => {
    try {
      const resolved = await fetchFileInfoBatch(batch);
      if (!resolved) {
        const oneByOne = await mapWithConcurrency(batch, concurrency, async (url) => {
          try {
            return await fetchFileInfo(url);
          } catch {
            return null;
          }
        });
        batch.forEach((url, index) => summaries.set(url, oneByOne[index]));
        return;
      }
      for (const url of batch) summaries.set(url, resolved.get(url) ?? null);
    } catch (error) {
      if (batch.length > 1 && isRejectedPath(error)) {
        const half = Math.ceil(batch.length / 2);
        await run(batch.slice(0, half));
        await run(batch.slice(half));
        return;
      }
      for (const url of batch) summaries.set(url, null);
    }
  };
  for (let start = 0; start < urls.length; start += PROVENANCE_FILE_INFO_BATCH_LIMIT) {
    await run(urls.slice(start, start + PROVENANCE_FILE_INFO_BATCH_LIMIT));
  }
  return summaries;
}

/**
 * Provenance summaries for every row across ``mediaGroups`` that has something
 * to resolve, keyed by the lookup url that produced them. Empty when no row
 * does, which callers use to hand their input straight back untouched.
 */
async function fetchModerationSummaries(
  mediaGroups: Iterable<MediaSource[] | undefined>,
  fetchFileInfo: TimelineFileInfoFetcher,
  options: ModerationLookupOptions | undefined,
): Promise<Map<string, FileSummary | null>> {
  const urls = new Set<string>();
  for (const media of mediaGroups) {
    for (const row of media ?? []) {
      const url = timelineModerationLookupUrl(row);
      if (url) urls.add(url);
    }
  }
  if (urls.size === 0) return new Map();

  const uniqueUrls = [...urls];
  const concurrency = options?.concurrency ?? DEFAULT_CONCURRENCY;
  // List callers must pass fetchFileInfoBatch. Without it this falls through
  // to one GET /files/file-info per URL — do not drop the batch option.
  if (options?.fetchFileInfoBatch) {
    return fetchBatchSummaries(uniqueUrls, fetchFileInfo, options.fetchFileInfoBatch, concurrency);
  }
  const summaries = await mapWithConcurrency(uniqueUrls, concurrency, async (url) => {
    try {
      return await fetchFileInfo(url);
    } catch {
      return null;
    }
  });
  return new Map(uniqueUrls.map((url, index) => [url, summaries[index]]));
}

/**
 * ``media`` with every moderated row moved onto the rendition its summary
 * resolves to, or the same array when no row moves.
 *
 * Rows whose file has no finding, rows already pointing at the right rendition,
 * and rows whose lookup failed are returned untouched: a file with nothing to
 * hide keeps today's behaviour exactly, and a provenance outage degrades to the
 * previously resolved rendition rather than an empty timeline (TR-2153).
 */
function applyModerationToMedia(
  media: MediaSource[],
  infoByUrl: Map<string, FileSummary | null>,
): MediaSource[] {
  let changed = false;
  const resolved = media.map((row) => {
    const url = timelineModerationLookupUrl(row);
    const info = url ? infoByUrl.get(url) ?? null : null;
    if (!info || !hasModerationFinding(info)) return row;
    const rendition = trimmed(info.previewUrl);
    // No rendition to move to (a flagged file whose obfuscated copy has not
    // been produced yet) leaves the row as it is rather than blanking it: the
    // row is already showing whatever provenance last resolved it to.
    if (!rendition || rendition === url) return row;
    changed = true;
    return withRendition(row, rendition, info);
  });
  return changed ? resolved : media;
}

/**
 * Point a standalone set of media rows at the rendition provenance resolves
 * each one to now, for callers holding media outside a loaded timeline.
 *
 * Rows built in the browser from chat citations are the case this exists for.
 * A citation carries the url of the object it cites, which for an extract
 * inside a container is the extract itself rather than the moderation-safe
 * preview provenance would hand back — so a flagged still reaches the Add Event
 * modal unobfuscated and is saved that way. Running the rows through here first
 * means the event previews, and stores, the same rendition
 * {@link resolveTimelineMediaModeration} would give it on the next load.
 */
export async function resolveMediaModeration(
  media: MediaSource[],
  fetchFileInfo: TimelineFileInfoFetcher,
  options?: ModerationLookupOptions,
): Promise<MediaSource[]> {
  const infoByUrl = await fetchModerationSummaries([media], fetchFileInfo, options);
  if (infoByUrl.size === 0) return media;
  return applyModerationToMedia(media, infoByUrl);
}

/**
 * Attach the current provenance summary to every content-moderated image row on
 * a loaded timeline, so each one renders the rendition its file resolves to
 * *now* rather than the one it resolved to when the event was saved.
 *
 * A media row persists a display URL, which names a derived rendition and so
 * records the file's moderation state at save time. Flag a file after its event
 * was saved and the stored URL still addresses the un-obfuscated rendition;
 * clear the finding and it still addresses the blurred copy. Nothing downstream
 * re-checks: the events table, timeline cards, courtroom view and media viewer
 * all render the saved row as-is.
 *
 * Only rows provenance reports a content-moderation finding for are rewritten,
 * and only to the rendition provenance currently resolves them to — see
 * {@link withRendition} for why the row's URLs have to move rather than just
 * gaining a ``fileInfo``, and {@link applyModerationToMedia} for which rows are
 * left exactly as saved (TR-2153).
 */
export async function resolveTimelineMediaModeration(
  slides: Slide[],
  fetchFileInfo: TimelineFileInfoFetcher,
  options?: ModerationLookupOptions,
): Promise<Slide[]> {
  const infoByUrl = await fetchModerationSummaries(
    slides.map((slide) => slide.media),
    fetchFileInfo,
    options,
  );
  if (infoByUrl.size === 0) return slides;

  return slides.map((slide) => {
    if (!slide.media?.length) return slide;
    const media = applyModerationToMedia(slide.media, infoByUrl);
    return media === slide.media ? slide : { ...slide, media };
  });
}
